import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { assemblyLineService } from "~/services/production";
import type { AssemblyLineInput } from "~/types/production";

const QUERY_KEY = "assembly_lines";

/**
 * Fetch assembly lines (Líneas de Envasado) for dropdowns.
 * Returns only active lines unless `includeInactive` is true.
 */
export function useAssemblyLines(includeInactive = false) {
  return useQuery({
    queryKey: [QUERY_KEY, { includeInactive }],
    queryFn: () => assemblyLineService.getAll(includeInactive),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateAssemblyLine() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: AssemblyLineInput) => assemblyLineService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useUpdateAssemblyLine() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: Partial<AssemblyLineInput>;
    }) => assemblyLineService.update(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useDeleteAssemblyLine() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => assemblyLineService.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}
